import React, { useState, useEffect, useCallback } from 'react';
import { estadoDeDocumento, borradoEnCurso, mensajeDeBorrado } from './estadoDeDocumento';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './KnowledgeDocuments.css';

export default function KnowledgeDocuments({ botId, organizationId }) {
  const { t } = useTranslation('bots');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null); // Track which document is being deleted
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const functions = getFunctions();

  // Load documents on mount
  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const listDocs = httpsCallable(functions, 'listKnowledgeDocuments');
      const result = await listDocs({ botId, organizationId });

      const lista = result.data.documents || [];
      setDocuments(lista);
      // Se devuelve además de guardarse en el estado: `handleDeleteDocument`
      // necesita mirarla EN ESE MOMENTO para decidir el mensaje, y el estado de
      // React no está disponible hasta el siguiente render.
      return lista;
    } catch (err) {
      console.error('Error loading documents:', err);
      // Don't show error for "not found" -- just means no docs yet
      if (!err.message?.includes('not found')) {
        setError(t('documents.loadError', { error: err.message }));
      }
    } finally {
      setLoading(false);
    }
  }, [botId, functions, t]);

  useEffect(() => {
    if (botId) {
      loadDocuments();
    }
  }, [botId, loadDocuments]);

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const validExtensions = ['xlsx', 'xls', 'csv', 'pdf', 'docx', 'doc', 'txt'];
    const fileExt = file.name.toLowerCase().split('.').pop();

    if (!validExtensions.includes(fileExt)) {
      setError(t('documents.unsupportedType', { ext: fileExt, allowed: validExtensions.join(', ') }));
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(t('documents.fileTooLarge'));
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Convert file to base64
      const base64 = await fileToBase64(file);

      // Call upload function
      const uploadDoc = httpsCallable(functions, 'uploadKnowledgeDocument');
      const result = await uploadDoc({
        botId,
        organizationId,
        filename: file.name,
        fileData: base64,
        metadata: {
          size: file.size,
          lastModified: new Date(file.lastModified).toISOString(),
        },
      });

      setSuccess(t('documents.uploadSuccess', { name: file.name }));

      // Reload documents after a short delay
      setTimeout(() => {
        loadDocuments();
      }, 1500);
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(t('documents.uploadError', { error: err.message }));
    } finally {
      setUploading(false);
    }
  };

  // Handle file deletion
  const handleDeleteDocument = async (docId, title) => {
    if (!window.confirm(t('documents.confirmDelete', { title }))) {
      return;
    }

    setError(null);
    setSuccess(null);
    setDeleting(docId); // Mark document as being deleted

    try {
      const deleteDoc = httpsCallable(functions, 'deleteKnowledgeDocument');
      await deleteDoc({ botId, docId, organizationId });

      // AVISO DIFERIDO, y sólo SI TARDA. El callable de R2b borra hasta 2.000
      // chunks y, si no cabe, marca el documento como `deleting` y cede el
      // resto al barrido programado. En el caso rápido —el habitual— el
      // documento ya no está y decir «continúa en segundo plano» sería mentir.
      //
      // Por eso el mensaje lo decide la LISTA RECARGADA, no una suposición: si
      // el documento sigue ahí en estado `deleting`, es que se difirió.
      const docs = await loadDocuments();
      setSuccess(t(mensajeDeBorrado(docs, docId), { title }));
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(t('documents.deleteError', { error: err.message }));
    } finally {
      setDeleting(null); // Clear deleting state
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();

    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get status badge
  const getStatusBadge = (status) => {
    // La decisión vive en `estadoDeDocumento.js`, con su porqué y sus tests.
    const badge = estadoDeDocumento(status);
    return (
      <span className={`status-badge status-${badge.color}`}>
        {t(badge.clave)}
      </span>
    );
  };

  // Get file type icon
  const getFileTypeIcon = (type) => {
    const icons = {
      excel: '📊',
      csv: '📊',
      pdf: '📄',
      word: '📝',
      text: '📃',
    };
    return icons[type] || '📎';
  };

  return (
    <div className="knowledge-documents">
      <div className="knowledge-documents-header">
        <h3>{t('documents.title')}</h3>
        <p className="knowledge-documents-description">
          {t('documents.description')}
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="message message-error">
          {error}
          <button onClick={() => setError(null)} className="message-close">x</button>
        </div>
      )}

      {success && (
        <div className="message message-success">
          {success}
          <button onClick={() => setSuccess(null)} className="message-close">x</button>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`upload-area ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="fileInput"
          className="file-input"
          accept=".xlsx,.xls,.csv,.pdf,.docx,.doc,.txt"
          onChange={handleChange}
          disabled={uploading}
        />

        {uploading ? (
          <div className="upload-content">
            <div className="spinner"></div>
            <p>{t('documents.uploading')}</p>
          </div>
        ) : (
          <label htmlFor="fileInput" className="upload-content">
            <div className="upload-icon">📤</div>
            <p className="upload-text">
              <strong>{t('documents.dragDrop')}</strong> {t('documents.orClick')}
            </p>
            <p className="upload-hint">
              {t('documents.formats')}
              <br />
              {t('documents.maxSize')}
            </p>
          </label>
        )}
      </div>

      {/* Documents List */}
      <div className="documents-list">
        <div className="documents-list-header">
          <h4>{t('documents.uploadedDocs', { count: documents.length })}</h4>
          {documents.length > 0 && (
            <button onClick={loadDocuments} className="btn-refresh" disabled={loading}>
              {t('documents.refresh')}
            </button>
          )}
        </div>

        {loading && documents.length === 0 ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t('documents.loadingDocs')}</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <p>{t('documents.emptyState')}</p>
            <p className="empty-state-hint">
              {t('documents.emptyStateHint')}
            </p>
          </div>
        ) : (
          <div className="documents-table">
            {documents.map((doc) => (
              <div key={doc.id} className="document-row">
                <div className="document-info">
                  <div className="document-icon">
                    {getFileTypeIcon(doc.type)}
                  </div>
                  <div className="document-details">
                    <div className="document-title">{doc.title}</div>
                    <div className="document-meta">
                      {formatFileSize(doc.size)} • {doc.chunk_count || 0} chunks
                      {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                    </div>
                    {doc.error_message && (
                      <div className="document-error">{doc.error_message}</div>
                    )}
                  </div>
                </div>

                <div className="document-actions">
                  {getStatusBadge(doc.embedding_status)}
                  <button
                    onClick={() => handleDeleteDocument(doc.id, doc.title)}
                    className="btn-delete"
                    /* Dos fuentes de «se está borrando», y hacen falta las dos:
                       `deleting === doc.id` es la llamada en vuelo desde ESTA
                       pestaña, y `embedding_status === 'deleting'` es el estado
                       del servidor — que sobrevive a recargar la página y lo ven
                       también los demás miembros de la organización. Sin el
                       segundo, la papelera vuelve a habilitarse al refrescar y se
                       puede pedir el borrado de algo que ya se está borrando. */
                    disabled={borradoEnCurso(doc, deleting)}
                    title={borradoEnCurso(doc, deleting) ? t('documents.deleting') : t('documents.deleteBtn')}
                  >
                    {borradoEnCurso(doc, deleting) ? (
                      <>
                        <span className="spinner-small"></span>
                        <span style={{fontSize: '0.75rem', marginLeft: '4px'}}>{t('documents.deletingShort')}</span>
                      </>
                    ) : (
                      '🗑️'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
